import { ComponentProps, MouseEventHandler } from 'react';
import styled from 'styled-components';

import { Icon } from '@creditornot/alchemy-react';
import * as icons from '@creditornot/alchemy-react/icons';
import { colors } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import { Tag } from '@creditornot/cb-tag';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

import { PopoverContent } from './PopoverContent';

type TagProps = ComponentProps<typeof Tag>;

const TagLabel = styled.span`
  font-weight: 700;
  margin-right: 4px;
  &::after {
    content: ':';
  }
`;

const bgSize = 18;

const RemoveButtonBackground = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  border-radius: 50%;
  transition: transform 96ms ease-in;
  z-index: 1;
`;

const RemoveButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  margin-right: -4px;
  display: flex;
  position: relative;
  flex-shrink: 0;
  z-index: 0;

  width: ${bgSize}px;
  height: ${bgSize}px;
  border-radius: 50%;
  cursor: pointer;

  ${RemoveButtonBackground} {
    background-color: transparent;
    transform: scale(0);
  }

  &:hover ${RemoveButtonBackground} {
    background-color: ${colors.wolt8};
    transform: scale(1);
  }

  &:active ${RemoveButtonBackground} {
    background-color: ${colors.wolt16};
  }
`;

const IconContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: transparent;
  overflow: hidden;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Props = {
  className?: string;
  label?: string;
  removePopoverContent?: string;
  children?: React.ReactNode;
  onRemoveClick: MouseEventHandler<HTMLButtonElement>;
  size?: TagProps['size'];
};

export const FilterTag: React.FC<Props> = ({
  label,
  removePopoverContent,
  children,
  className,
  onRemoveClick,
  size = 'small',
}) => {
  const format = useFormatMessage();
  const renderRemoveButton = () => (
    <RemoveButton
      aria-label={format('components.filter_tag.remove_filter', { label })}
      onClick={onRemoveClick}
    >
      <RemoveButtonBackground />
      <IconContainer>
        <Icon source={icons.cross} size={size} />
      </IconContainer>
    </RemoveButton>
  );

  return (
    <Tag
      variant="lightBlue"
      className={className}
      icon={
        removePopoverContent ? (
          <Popover
            placement="top"
            openOnHover={true}
            content={<PopoverContent>{removePopoverContent}</PopoverContent>}
            zIndex={10}
          >
            {({ refProps }) => <div {...refProps}>{renderRemoveButton()}</div>}
          </Popover>
        ) : (
          renderRemoveButton()
        )
      }
      iconPosition="end"
      size={size}
    >
      {label && <TagLabel>{label}</TagLabel>}
      {children}
    </Tag>
  );
};
